import { c as _c } from "react/compiler-runtime";
import { type FC, type MouseEventHandler, forwardRef } from "react";
import type { Placement } from "@floating-ui/react";
import { type IconDefinition, faListOl, faListUl, faLink, faBold, faUnderline, faItalic, faCode, faStrikethrough, faSubscript, faSuperscript, faIndent, faOutdent, faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify, faImage, faH1, faBlockQuote, faH2, faH3, faVideo, faCaretDown, faSidebar } from "@fortawesome/pro-solid-svg-icons";
import { KEYS } from "@platejs/utils";
import { useMarkToolbarButton, useMarkToolbarButtonState, useSelectionFragmentProp } from "@platejs/utils/react";
import { useEditorState, ParagraphPlugin, PlateEditor, useEditorPlugin } from "@platejs/core/react";
import { BlockquotePlugin, BoldPlugin, CodePlugin, ItalicPlugin, StrikethroughPlugin, SubscriptPlugin, SuperscriptPlugin, UnderlinePlugin } from "@platejs/basic-nodes/react";
import { BulletedListPlugin, NumberedListPlugin, useListToolbarButton, useListToolbarButtonState } from "@platejs/list-classic/react";
import { useLinkToolbarButton, useLinkToolbarButtonState } from "@platejs/link/react";
import { useIndentButton, useOutdentButton } from "@platejs/indent/react";
import type { Alignment } from "@platejs/basic-styles";
import { ImagePlugin, MediaEmbedPlugin, useMediaToolbarButton } from "@platejs/media/react";
import type { NotePopulated } from "@vita-learning/types";
import { capitalize } from "~/shared/utils";
import { Dropdown } from "../lists";
import { Icon, Tooltip } from "../widgets";
import { StyledButton, StyledButtonContainer, StyledIcon, StyledToolbar } from "./styles";
import { TextAlignPlugin } from "@platejs/basic-styles/react";
interface BaseToolbarButtonProps {
  readonly icon: IconDefinition;
  readonly tooltip: string;
  readonly disabled?: boolean;
  readonly placement?: Placement;
}
interface ToolbarButtonProps extends BaseToolbarButtonProps {
  readonly pressed?: boolean;
  readonly onClick: MouseEventHandler<HTMLButtonElement>;
}
const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>((t0, ref) => {
  const $ = _c(12);
  const {
    onClick,
    pressed: t1,
    icon,
    tooltip,
    disabled,
    placement: t2
  } = t0;
  const pressed = t1 === undefined ? false : t1;
  const placement = t2 === undefined ? "top" : t2;
  let t3;
  if ($[0] !== icon) {
    t3 = <Icon icon={icon} />;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== disabled || $[3] !== onClick || $[4] !== pressed || $[5] !== ref || $[6] !== t3) {
    t4 = <StyledButton onClick={onClick} $pressed={pressed} disabled={disabled} ref={ref}>{t3}</StyledButton>;
    $[2] = disabled;
    $[3] = onClick;
    $[4] = pressed;
    $[5] = ref;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== placement || $[9] !== t4 || $[10] !== tooltip) {
    t5 = <Tooltip text={tooltip} placement={placement}>{t4}</Tooltip>;
    $[8] = placement;
    $[9] = t4;
    $[10] = tooltip;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
});
ToolbarButton.displayName = "ToolbarButton";
type ElementNodeType = typeof ParagraphPlugin.key | typeof KEYS.h1 | typeof KEYS.h2 | typeof KEYS.h3 | typeof BlockquotePlugin.key;
interface ElementToolbarButtonProps extends BaseToolbarButtonProps {
  readonly nodeType?: ElementNodeType;
}
const getElementPressed = (editor: PlateEditor, nodeType: ElementNodeType): boolean => {
  if (!editor.api.isCollapsed()) return false;
  const entry = editor.api.node({
    match: n => editor.api.isBlock(n) && n.type === nodeType
  });
  if (!entry) return false;
  return entry[0].type === nodeType;
};
const ElementToolbarButton: FC<ElementToolbarButtonProps> = t0 => {
  const $ = _c(12);
  const {
    nodeType: t1,
    icon,
    tooltip,
    disabled
  } = t0;
  const nodeType = t1 === undefined ? ParagraphPlugin.key : t1;
  const editor = useEditorState();
  let t2;
  if ($[0] !== editor || $[1] !== nodeType) {
    t2 = getElementPressed(editor, nodeType);
    $[0] = editor;
    $[1] = nodeType;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const pressed = t2;
  let t3;
  if ($[3] !== editor.tf || $[4] !== nodeType) {
    t3 = () => {
      editor.tf.toggleBlock(nodeType);
      editor.tf.collapse();
      editor.tf.focus();
    };
    $[3] = editor.tf;
    $[4] = nodeType;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== disabled || $[7] !== icon || $[8] !== pressed || $[9] !== t3 || $[10] !== tooltip) {
    t4 = <ToolbarButton disabled={disabled} pressed={pressed} icon={icon} tooltip={tooltip} onClick={t3} />;
    $[6] = disabled;
    $[7] = icon;
    $[8] = pressed;
    $[9] = t3;
    $[10] = tooltip;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
interface ListToolbarButtonProps extends BaseToolbarButtonProps {
  readonly nodeType?: typeof BulletedListPlugin.key | typeof NumberedListPlugin.key;
}
const ListToolbarButton: FC<ListToolbarButtonProps> = t0 => {
  const $ = _c(8);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      nodeType: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const nodeType = t1 === undefined ? BulletedListPlugin.key : t1;
  let t2;
  if ($[3] !== nodeType) {
    t2 = {
      nodeType
    };
    $[3] = nodeType;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const state = useListToolbarButtonState(t2);
  const {
    props: buttonProps
  } = useListToolbarButton(state);
  let t3;
  if ($[5] !== buttonProps || $[6] !== props) {
    t3 = <ToolbarButton {...buttonProps} {...props} />;
    $[5] = buttonProps;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
interface AlignToolbarButtonProps {
  readonly disabled: boolean;
  readonly icon: IconDefinition;
  readonly value: Alignment;
}
const AlignToolbarButton: FC<AlignToolbarButtonProps> = t0 => {
  const $ = _c(14);
  const {
    disabled,
    icon,
    value
  } = t0;
  const {
    editor,
    tf
  } = useEditorPlugin(TextAlignPlugin);
  const alignValue = useSelectionFragmentProp({
    defaultValue: "start",
    getProp: _temp
  }) ?? "left";
  let t1;
  if ($[0] !== editor.tf || $[1] !== tf.textAlign) {
    t1 = alignment => {
      tf.textAlign.setNodes(alignment);
      editor.tf.focus();
    };
    $[0] = editor.tf;
    $[1] = tf.textAlign;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleValueChange = t1;
  const t2 = value === alignValue;
  let t3;
  if ($[3] !== value) {
    t3 = capitalize(value);
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== handleValueChange || $[6] !== value) {
    t4 = () => handleValueChange(value);
    $[5] = handleValueChange;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== disabled || $[9] !== icon || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <ToolbarButton pressed={t2} disabled={disabled} icon={icon} tooltip={t3} onClick={t4} placement="right" />;
    $[8] = disabled;
    $[9] = icon;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
interface AlignmentDropdownProps {
  readonly disabled: boolean;
}
const AlignmentDropdown: FC<AlignmentDropdownProps> = t0 => {
  const $ = _c(22);
  const {
    disabled
  } = t0;
  const alignValue = useSelectionFragmentProp({
    defaultValue: "start",
    getProp: _temp2
  }) ?? "left";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      left: faAlignLeft,
      center: faAlignCenter,
      right: faAlignRight,
      justify: faAlignJustify,
      start: faAlignLeft,
      end: faAlignRight
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const ALIGNMENT_ICON_MAP = t1;
  let t2;
  if ($[1] !== disabled) {
    t2 = {
      label: "Left",
      renderItem: () => <AlignToolbarButton disabled={disabled} icon={faAlignLeft} value="left" />
    };
    $[1] = disabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== disabled) {
    t3 = {
      label: "Center",
      icon: faAlignCenter,
      renderItem: () => <AlignToolbarButton disabled={disabled} icon={faAlignCenter} value="center" />
    };
    $[3] = disabled;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== disabled) {
    t4 = {
      label: "Right",
      renderItem: () => <AlignToolbarButton disabled={disabled} icon={faAlignRight} value="right" />
    };
    $[5] = disabled;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== disabled) {
    t5 = {
      label: "Justify",
      icon: faAlignJustify,
      renderItem: () => <AlignToolbarButton disabled={disabled} icon={faAlignJustify} value="justify" />
    };
    $[7] = disabled;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = [t2, t3, t4, t5];
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = ALIGNMENT_ICON_MAP[alignValue as keyof typeof ALIGNMENT_ICON_MAP];
  let t8;
  if ($[14] !== t7) {
    t8 = <StyledIcon icon={t7} />;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <StyledIcon icon={faCaretDown} />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8) {
    t10 = <Tooltip text="Alignment" placement="top">{t8}{" "}{t9}</Tooltip>;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <Dropdown items={t6}>{t10}</Dropdown>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
interface MarkToolbarButtonProps extends BaseToolbarButtonProps {
  readonly nodeType: typeof BoldPlugin.key | typeof UnderlinePlugin.key | typeof ItalicPlugin.key | typeof SubscriptPlugin.key | typeof SuperscriptPlugin.key | typeof StrikethroughPlugin.key | typeof CodePlugin.key;
  readonly clear?: string | string[];
}
const MarkToolbarButton: FC<MarkToolbarButtonProps> = t0 => {
  const $ = _c(10);
  let clear;
  let nodeType;
  let props;
  if ($[0] !== t0) {
    ({
      clear,
      nodeType,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = clear;
    $[2] = nodeType;
    $[3] = props;
  } else {
    clear = $[1];
    nodeType = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== clear || $[5] !== nodeType) {
    t1 = {
      clear,
      nodeType
    };
    $[4] = clear;
    $[5] = nodeType;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const state = useMarkToolbarButtonState(t1);
  const {
    props: buttonProps
  } = useMarkToolbarButton(state);
  let t2;
  if ($[7] !== buttonProps || $[8] !== props) {
    t2 = <ToolbarButton {...buttonProps} {...props} />;
    $[7] = buttonProps;
    $[8] = props;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
const IndentToolbarButton: FC<{
  disabled: boolean;
}> = t0 => {
  const $ = _c(3);
  const {
    disabled
  } = t0;
  const {
    props
  } = useIndentButton();
  let t1;
  if ($[0] !== disabled || $[1] !== props) {
    t1 = <ToolbarButton disabled={disabled} tooltip="Indent" icon={faIndent} {...props} />;
    $[0] = disabled;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const OutdentToolbarButton: FC<{
  disabled: boolean;
}> = t0 => {
  const $ = _c(3);
  const {
    disabled
  } = t0;
  const {
    props
  } = useOutdentButton();
  let t1;
  if ($[0] !== disabled || $[1] !== props) {
    t1 = <ToolbarButton disabled={disabled} tooltip="Outdent" icon={faOutdent} {...props} />;
    $[0] = disabled;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const LinkToolbarButton: FC<{
  disabled: boolean;
}> = t0 => {
  const $ = _c(3);
  const {
    disabled
  } = t0;
  const state = useLinkToolbarButtonState();
  const {
    props
  } = useLinkToolbarButton(state);
  let t1;
  if ($[0] !== disabled || $[1] !== props) {
    t1 = <ToolbarButton disabled={disabled} tooltip="Link" icon={faLink} {...props} />;
    $[0] = disabled;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
interface MediaToolbarButtonProps extends BaseToolbarButtonProps {
  readonly nodeType?: typeof ImagePlugin.key | typeof MediaEmbedPlugin.key;
}
const MediaToolbarButton: FC<MediaToolbarButtonProps> = t0 => {
  const $ = _c(8);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      nodeType: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const nodeType = t1 === undefined ? ImagePlugin.key : t1;
  let t2;
  if ($[3] !== nodeType) {
    t2 = {
      nodeType
    };
    $[3] = nodeType;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    props: buttonProps
  } = useMediaToolbarButton(t2);
  let t3;
  if ($[5] !== buttonProps || $[6] !== props) {
    t3 = <ToolbarButton {...buttonProps} {...props} />;
    $[5] = buttonProps;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
interface ToolbarProps {
  readonly note?: NotePopulated | null;
  readonly sidebar: boolean;
  readonly isSidebarOpen: boolean;
  readonly onToggleSidebar?: () => void;
}
export const Toolbar: FC<ToolbarProps> = t0 => {
  const $ = _c(26);
  const {
    note,
    isSidebarOpen,
    onToggleSidebar,
    sidebar
  } = t0;
  const disabled = !note;
  let t1;
  if ($[0] !== isSidebarOpen || $[1] !== onToggleSidebar || $[2] !== sidebar) {
    t1 = sidebar && <Tooltip text={isSidebarOpen ? "Hide Sidebar" : "Show Sidebar"} placement="top"><StyledButton onClick={onToggleSidebar}><Icon icon={faSidebar} /></StyledButton></Tooltip>;
    $[0] = isSidebarOpen;
    $[1] = onToggleSidebar;
    $[2] = sidebar;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== disabled) {
    t2 = <StyledButtonContainer><ElementToolbarButton disabled={disabled} nodeType={KEYS.h1} icon={faH1} tooltip={"Heading 1 (\u2318\u23251)"} /><ElementToolbarButton disabled={disabled} nodeType={KEYS.h2} icon={faH2} tooltip={"Heading 2 (\u2318\u23252)"} /><ElementToolbarButton disabled={disabled} nodeType={KEYS.h3} icon={faH3} tooltip={"Heading 3 (\u2318\u23253)"} /><ElementToolbarButton disabled={disabled} nodeType={BlockquotePlugin.key} icon={faBlockQuote} tooltip="Blockquote" /></StyledButtonContainer>;
    $[4] = disabled;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== disabled) {
    t3 = <StyledButtonContainer><MarkToolbarButton disabled={disabled} tooltip={"Bold (\u2318+B)"} nodeType={BoldPlugin.key} icon={faBold} /><MarkToolbarButton disabled={disabled} tooltip={"Italic (\u2318+I)"} nodeType={ItalicPlugin.key} icon={faItalic} /><MarkToolbarButton disabled={disabled} tooltip={"Underline (\u2318+U)"} nodeType={UnderlinePlugin.key} icon={faUnderline} /><MarkToolbarButton disabled={disabled} tooltip={"Strikethrough (\u2318+\u21E7+M)"} nodeType={StrikethroughPlugin.key} icon={faStrikethrough} /><MarkToolbarButton disabled={disabled} tooltip="Superscript" nodeType={SuperscriptPlugin.key} icon={faSubscript} /><MarkToolbarButton disabled={disabled} tooltip="Subscript" nodeType={SubscriptPlugin.key} icon={faSuperscript} /><MarkToolbarButton disabled={disabled} tooltip={"Code (\u2318+E)"} nodeType={CodePlugin.key} icon={faCode} /></StyledButtonContainer>;
    $[6] = disabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== disabled) {
    t4 = <StyledButtonContainer><OutdentToolbarButton disabled={disabled} /><IndentToolbarButton disabled={disabled} /></StyledButtonContainer>;
    $[8] = disabled;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== disabled) {
    t5 = <StyledButtonContainer><ListToolbarButton disabled={disabled} nodeType={BulletedListPlugin.key} icon={faListUl} tooltip="Bullet list" /><ListToolbarButton disabled={disabled} nodeType={NumberedListPlugin.key} icon={faListOl} tooltip="Number list" /></StyledButtonContainer>;
    $[10] = disabled;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== disabled) {
    t6 = <StyledButtonContainer><AlignmentDropdown disabled={disabled} /></StyledButtonContainer>;
    $[12] = disabled;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  let t8;
  if ($[14] !== disabled) {
    t7 = <StyledButtonContainer><MediaToolbarButton disabled={disabled} icon={faImage} nodeType={ImagePlugin.key} tooltip="Image" /><MediaToolbarButton disabled={disabled} icon={faVideo} nodeType={MediaEmbedPlugin.key} tooltip="Embed Media" /></StyledButtonContainer>;
    t8 = <LinkToolbarButton disabled={disabled} />;
    $[14] = disabled;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <StyledToolbar>{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}</StyledToolbar>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
function _temp(node) {
  return node.align;
}
function _temp2(node) {
  return node.align;
}